import { DealRelation, DealStatus, DealType, DealSourceType, EntityType } from './deal.enums';

export interface DealDetail {
  id?: number | string;
  entity1: string;
  entity1_type: string;
  entity1_vtr_id?: string;
  entity2: string;
  entity2_type: string;
  entity2_vtr_id?: string;
  content?: string;
  content_vtr_id?: string;
  status: DealStatus | `${DealStatus}`;
  published_date: string;
  confidence: number;
  relation: DealRelation;
  source_url?: string;
  deal_territory?: string;
  sentences?: DealSentence[];
  deal_type?: DealType | `${DealType}` | string;
  deal_duration?: string;
  deal_value?: string;
  deal_season?: string;
  deal_badge?: MasterData;
  comment?: string;
  entity1_imdb_id?: string;
  entity2_imdb_id?: string;
  deal_content_genre?: string;
  type?: string;
  country_of_origin?: string;
  book_name?: string;
  additional_comments?: string;
  working_title?: string;
  delete_reason?: string;
  original_data?: string;
  multiparty_deal?: boolean;
  deal_source_id?: number;
  deal_source?: DealSource;
  score?: DealScore;
  updated_at?: string;
  updated_at_filter?: string;
  updated_by?: string;
  created_at?: string;
  created_by?: string;
  announcement_market?: string;
  platform_type?: DealEntity[];
  to_air_on?: DealEntity[];
  buyers?: DealEntity[];
  sellers?: DealEntity[];
  contents?: DealEntity[];
  event?: string;
  filming_location?: [];
  associated_person?: AssociatedPerson[];
  deal_subtype?: MasterData[];
  excluded_country?: MasterData[];
  excluded_region?: MasterData[];
  included_country?: MasterData[];
  included_region?: MasterData[];
  international_domestic?: string;
  deal_announcement_stage?: string;
  mapped_countries?: MasterData[];
  rights_type?: MasterData[];
  language_rights?: MasterData[];
  tags?: MasterData[];
  package_deal_details?: string;
  child_relation?: DealChildRelation[];
  deal_status?: string;
  deal_highlight?: string;
  content_highlight?: string;
  deal_expiry_date?: Date | string;
  contentHightlight?: string;
  person?:AssociatedPerson[];
  deal_content_status?: string;
}
// export interface AssociatedPerson {
//   name?: string;
//   vtr_id?: string;
//   designation?: string;
//   company?: string;
//   person_vtr_id: string;
//   company_vtr_id:string;
//   company_name:string;
//   relation_type:string;
//   type:string;
//   order_id:number;
// }

export interface PersonCompanyRelation{
  person_vtr_id?: string;
  company_vtr_id?: string,
  designation?: string;
}

export interface AssociatedPerson {
  name?: string;
  vtr_id?: string;
  designation?: string;
  company?: string;
  person_vtr_id: string;
  company_name?:string;
  company_vtr_id?:string;
  order_id?:number;
  type?:string;
  relation_type?:string;
  meta?:any;
}

type ExcludeInMultiparty =
  | `entity${1 | 2}`
  | `entity${1 | 2}_type`
  | `entity${1 | 2}_vtr_id`
  | `entity${1 | 2}_imdb_id`
  | 'content'
  | 'content_vtr_id'
  | 'buyers'
  | 'sellers'
  | 'contents';
export enum DealPartyRelationType {
  buyer = 'buyer',
  seller = 'seller',
  content = 'content',
  to_air_on = 'to_air_on',
  person = 'person'
}
export type DealPartyRelation = `${DealPartyRelationType}`;

/**
 * Deal Entities that is introduced by the multi party schema.
 */
export interface DealEntity {
  /**
   * The id of the relation. This is not available on
   * new relations.
   */
  id?: number;
  /**
   * The name of the entity.
   */
  name?: string;
  /**
   * The unique id of the enitity, if any.
   */
  vtr_id?: string;
  /**
   * They type of entity, can be CONTENT, COMPANY or PERSON
   */
  type?: `${EntityType}`;
  /**
   * The type of relation, 'buyer', 'seller' or 'content'
   */
  relation_type?: DealPartyRelation;
  /**
   * The imdb id of the entity.
   */
  imdb_id?: string;
  /**
   * The id of the deal having this relation.
   */
  deal_id?: number | string;
  /**
   * The ML confidence if this entity is coming from ML.
   */
  confidence?: number;
  /**
   * The content genre if the type of this entity is 'CONTENT'
   */
  deal_content_genre?: string;
  /**
   * The language of this content.
   */
  language?: string;
  /**
   * The country of origin for this content.
   */
  country_of_origin?: string;
  /**
   * The type of the content, TV Series/Movie
   */
  deal_content_type?: string;
  /**
   * The book name of the content.
   */
  book_name?: string;
  /**
   * The working title of the content
   */
  working_title?: string;
  /**
   * The season of the content.
   */
  deal_season?: string;
  /**
   * The order id of the entity
   */
  order_id?: number;
  /**
   * Original title of the content
   */
  original_title?: string;
  based_on?: string;
  local_content_name?: string;
  local_content_vtr_id?: string;
  cast_and_crew?: AssociatedPerson[];
  title ?: string;
  content_keywords? : string[];
  genre_category?: string;
  genres_id?: string;
  status?: string;
  language_id?: string;
  filming_location?: any[];
  origin_country?: "";
  content_id?:"";
  content_type?: Number;
}

// Aliases
export interface DealBuyer
  extends Pick<DealEntity, 'id' | 'deal_id' | 'name' | 'type' | 'relation_type' | 'confidence'> {}
export interface DealSeller extends DealBuyer {}
export interface DealToAirOn extends DealBuyer {}
export interface DealContent extends DealEntity {}

/**
 * Multi party deals. To retain compatibility with other attributes of the deal,
 * the multi party deals extends from the single party deal interface `DealDetail`
 */
export interface MultiPartyDealDetail extends Omit<DealDetail, ExcludeInMultiparty> {
  /**
   * List of buyers of this deal.
   */
  buyers?: DealEntity[];
  /**
   * List of sellers of this deal.
   */
  sellers?: DealEntity[];
  /**
   * List of to_air_on for this deal.
   */
  to_air_on?: DealEntity[];
  /**
   * List of content for this deal.
   */
  contents?: DealEntity[];
}

export interface DealScore {
  deal_score?: number;
}

export interface DealSentence {
  id?: number | string;
  text?: string;
  article?: DealArticle;
}

export interface DealArticle {
  id?: number | string;
  source_url?: string;
}

export interface DealSource {
  id?: number | string;
  deal_source_type?: DealSourceType | `${DealSourceType}`;
}

export interface MasterData {
  id: number | string;
  name: string;
}

export interface DealChildRelation {
  description?: string;
  vtr_id?: string;
}
